import { useEffect, useState } from 'react'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore'
import { db } from '../firebase'

export function useFirestoreCollection<T extends { id?: string }>(
  collectionName: string,
) {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const collectionRef = collection(db, collectionName)

    const unsubscribe = onSnapshot(
      collectionRef,
      (snapshot) => {
        const items: T[] = []
        snapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() } as T)
        })

        // Sort by createdAt in memory if available
        items.sort((a: any, b: any) => {
          const aTime = a.createdAt?.toMillis?.() || 0
          const bTime = b.createdAt?.toMillis?.() || 0
          return bTime - aTime
        })

        setData(items)
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Firestore error:', err)
        setError(err.message)
        setLoading(false)
      },
    )

    return () => unsubscribe()
  }, [collectionName])

  // Helper function to remove undefined values
  const cleanData = (obj: any): any => {
    const cleaned: any = {}
    Object.keys(obj).forEach((key) => {
      if (obj[key] !== undefined) {
        cleaned[key] = obj[key]
      }
    })
    return cleaned
  }

  const add = async (item: Omit<T, 'id'>) => {
    try {
      const cleanedItem = cleanData({
        ...item,
        createdAt: Timestamp.now(),
      })

      const docRef = await addDoc(collection(db, collectionName), cleanedItem)
      return docRef.id
    } catch (err: any) {
      console.error('Error adding document:', err)
      setError(err.message)
      throw err
    }
  }

  const update = async (id: string, item: Partial<T>) => {
    try {
      const cleanedItem = cleanData({
        ...item,
        updatedAt: Timestamp.now(),
      })

      const docRef = doc(db, collectionName, id)
      await updateDoc(docRef, cleanedItem)
    } catch (err: any) {
      console.error('Error updating document:', err)
      setError(err.message)
      throw err
    }
  }

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(db, collectionName, id))
    } catch (err: any) {
      console.error('Error deleting document:', err)
      setError(err.message)
      throw err
    }
  }

  return { data, loading, error, add, update, remove }
}
